// ============================================================================
// RegisterRecordTable.tsx — НАБОР ЗАПИСЕЙ регистра сведений (как в 1С:
// РегистрСведенийНаборЗаписей). Реквизит формы register_record с multiple=true:
//   - строки = записи регистра (измерения — отбор, ресурсы — колонки);
//   - измерения со связями («измерение ← источник», свойство реквизита)
//     подставляются автоматически (current_object — id записи, field — реквизит);
//   - «Записать» = полная замена набора по измерениям (как в 1С).
// SSoT — сервер: ListTableData/CreateTableRow/DeleteTableRow + GetRegister
// (структура измерений/ресурсов).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';

export interface RegisterRecordDimensionLink {
  code: string;
  source: '' | 'current_object' | 'field';
  field?: string;
}

interface RegisterRecordTableProps {
  registerCode: string;
  /** Связи измерений «измерение ← источник значения» (свойство реквизита формы) */
  dimensionLinks?: RegisterRecordDimensionLink[];
  /** Контекст значений: значения формы (по последнему сегменту пути) + id текущей записи */
  context: { values: Record<string, unknown>; recordId?: string };
  disabled?: boolean;
  onError?: (msg: string) => void;
}

interface RegCol {
  code: string;
  name: string;
  dataType: string;
  isDim: boolean;
  refCatalogCode?: string;
}

/** Резолв связей измерений: current_object → recordId (или values.id),
 *  field → значение реквизита формы по последнему сегменту пути. */
export function resolveRegisterDimensions(
  links: RegisterRecordDimensionLink[] | undefined,
  ctx: { values: Record<string, unknown>; recordId?: string },
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const link of links || []) {
    let v: unknown = undefined;
    if (link.source === 'current_object') {
      v = ctx.recordId || ctx.values.id;
    } else if (link.source === 'field' && link.field) {
      const key = link.field.split('.').pop() || link.field;
      v = ctx.values[key];
    }
    if (v !== undefined && v !== null && v !== '') out[link.code] = String(v);
  }
  return out;
}

export function RegisterRecordTable({ registerCode, dimensionLinks, context, disabled, onError }: RegisterRecordTableProps) {
  const [cols, setCols] = useState<RegCol[]>([]);
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  const dims = resolveRegisterDimensions(dimensionLinks, context);

  const reportError = (msg: string) => {
    setError(msg);
    onError?.(msg);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sdk = getSDK();
      // Структура регистра (SSoT — сервер): измерения + ресурсы
      if (cols.length === 0) {
        const reg: any = await sdk.metadata.getRegister({ id: registerCode });
        const structure: RegCol[] = [];
        for (const d of (reg?.dimensions || []) as any[]) {
          structure.push({ code: d.code || d.name, name: d.name || d.code, dataType: d.dataType || 'text', isDim: true, refCatalogCode: d.refCatalogCode || '' });
        }
        for (const r of (reg?.resources || []) as any[]) {
          structure.push({ code: r.code || r.name, name: r.name || r.code, dataType: r.dataType || 'text', isDim: false });
        }
        if (structure.length === 0) {
          reportError('Регистр не содержит измерений/ресурсов');
          setLoading(false);
          return;
        }
        setCols(structure);
      }
      // Строки по измерениям (серверные отборы — SSoT)
      const filters = Object.entries(dims).map(([field, value]) => ({ field, condition: '=', value: String(value) }));
      const r: any = await sdk.metadata.listTableData({ schema: 'public', table: registerCode, pageSize: 1000, page: 0, filters: filters as any });
      const recs: Array<Record<string, unknown>> = [];
      for (const row of r?.rows || []) {
        const rec: Record<string, unknown> = { id: row.id };
        for (const v of row.values || []) rec[v.column] = v.value;
        recs.push(rec);
      }
      setRows(recs);
    } catch (e: any) {
      reportError(e?.message || 'Ошибка загрузки набора записей');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerCode, JSON.stringify(dims)]);

  useEffect(() => { void load(); }, [load]);

  const addRow = () => {
    const row: Record<string, unknown> = { __new: true, ...dims };
    setRows((prev) => [...prev, row]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateCell = (idx: number, code: string, v: unknown) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [code]: v } : r)));
  };

  // «Записать» = полная замена набора по измерениям (как в 1С)
  const save = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const sdk = getSDK();
      const existingIds: string[] = [];
      const filters = Object.entries(dims).map(([field, value]) => ({ field, condition: '=', value: String(value) }));
      if (filters.length > 0) {
        const existing: any = await sdk.metadata.listTableData({ schema: 'public', table: registerCode, pageSize: 1000, page: 0, filters: filters as any });
        for (const row of existing?.rows || []) existingIds.push(row.id);
      }
      for (const id of existingIds) {
        await sdk.metadata.deleteTableRow({ schema: 'public', table: registerCode, rowId: id });
      }
      for (const rec of rows) {
        const payload = Object.entries(rec)
          .filter(([k, v]) => k !== 'id' && k !== '__new' && !['created_at', 'updated_at', 'created_by', 'updated_by', 'is_marked'].includes(k) && !String(k).endsWith('_presentation') && v !== undefined && v !== null && v !== '')
          .map(([column, value]) => ({ column, value: String(value) }));
        if (payload.length > 0) {
          await sdk.metadata.createTableRow({ schema: 'public', table: registerCode, values: payload as any });
        }
      }
      setSavedMsg('Сохранено');
      await load();
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e: any) {
      reportError(e?.message || 'Ошибка записи набора');
    } finally {
      setSaving(false);
    }
  };

  const isDimLinked = (code: string) => Object.prototype.hasOwnProperty.call(dims, code);
  const inputFor = (col: RegCol, idx: number, value: unknown) => {
    const v = String(value ?? '');
    const onChange = (next: unknown) => updateCell(idx, col.code, next);
    if (col.dataType === 'boolean') {
      return (
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={v === 'true' || v === '1' || value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    }
    if (col.dataType === 'numeric' || col.dataType === 'number' || col.dataType === 'integer') {
      return (
        <input
          type="number"
          className="h-6 w-full min-w-[70px] border rounded px-1 text-xs"
          value={v}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    if (col.dataType === 'date') {
      return (
        <input
          type="date"
          className="h-6 w-full min-w-[110px] border rounded px-1 text-xs"
          value={v.includes('T') ? v.split('T')[0] : v}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    if (col.dataType === 'boolean' || col.dataType === 'boolean') return null;
    return (
      <input
        type="text"
        className="h-6 w-full min-w-[90px] border rounded px-1 text-xs"
        value={v}
        placeholder={col.isDim && !isDimLinked(col.code) ? 'uuid' : ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  return (
    <div className="space-y-1 border rounded-lg p-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-medium">{cols.length > 0 ? 'Набор записей' : ''}</span>
        {Object.keys(dims).length > 0 && (
          <span className="text-[9px] text-muted-foreground font-mono truncate">
            {Object.entries(dims).map(([k, v]) => `${k}=${String(v).slice(0, 8)}…`).join(', ')}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {!disabled && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={addRow} disabled={loading || saving}>
              <Plus className="h-3 w-3 mr-0.5" />Строка
            </Button>
          )}
          {!disabled && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => void save()} disabled={loading || saving}>
              <Save className="h-3 w-3 mr-0.5" />{saving ? '…' : 'Записать'}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => void load()} disabled={loading || saving}>
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground p-2">Загрузка набора записей…</div>
      ) : error ? (
        <div className="text-[10px] text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1">{error}</div>
      ) : (
        <div className="overflow-auto border rounded-md">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/30">
                {cols.map((c) => (
                  <th key={c.code} className="px-1.5 py-1 text-left font-medium text-[9px] whitespace-nowrap" title={c.isDim ? 'Измерение' : 'Ресурс'}>
                    {c.name}
                    {c.isDim && <span className="text-muted-foreground"> (изм.)</span>}
                  </th>
                ))}
                {!disabled && <th className="px-1 w-6" />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={cols.length + (disabled ? 0 : 1)} className="px-2 py-2 text-[10px] text-muted-foreground italic">
                    Записей нет. Добавьте строку и нажмите «Записать».
                  </td>
                </tr>
              )}
              {rows.map((row, idx) => (
                <tr key={String(row.id || `new-${idx}`)} className="border-t">
                  {cols.map((c) => {
                    const locked = c.isDim && isDimLinked(c.code);
                    return (
                      <td key={c.code} className="px-1 py-0.5">
                        {locked ? (
                          <span className="text-[10px] font-mono text-muted-foreground truncate inline-block max-w-[120px]">{String(row[c.code] ?? '')}</span>
                        ) : (
                          inputFor(c, idx, row[c.code])
                        )}
                      </td>
                    );
                  })}
                  {!disabled && (
                    <td className="px-1 py-0.5 text-right">
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeRow(idx)} title="Удалить строку">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {savedMsg && <div className="text-[10px] text-green-600">✓ {savedMsg}</div>}
    </div>
  );
}
